import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useAtom } from 'jotai/index';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { code } from 'framer-motion/m';
import styles from './css/index.module.scss';
import ConfiguratorCategories from './components/ConfiguratorCategories';
import ConfiguratorMain from './components/ConfiguratorMain';
import useConfigurator from '../../hooks/useConfigurator';
import {
  configuratorCalculationsAtom,
  configuratorLayersListAtom,
  configuratorSelectedCityAtom,
  configuratorSingleConfigurationAtom,
  draggingPositionAtom,
  reloadConfigurationAfterInitAtom,
} from '../../atoms/configuratorAtoms';
import ConfigurationDetails from '../../components/ConfigurationDetails';
import { gettingSingleConfigurationLoadingAtom } from '../../atoms/configurationsAtoms';
import { closeModalAtom, openLoadingModalAtom } from '../../atoms/modalAtoms';
import { isInitAtom } from '../../atoms/globalAtoms';

function Configurator() {
  const {
    onDragStart, onDrag, onDragStop, onLayerClick, onRemoveLayerClick, getSingleConfiguration, refreshConfiguration,
  } = useConfigurator();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const configurationId = searchParams.get('configuration_id');
  const onlyDetails = searchParams.get('only_details') === 'true';

  const [isInit] = useAtom(isInitAtom);
  const [configuratorCalculations] = useAtom(configuratorCalculationsAtom);
  const [configuratorLayersList] = useAtom(configuratorLayersListAtom);
  const [draggingPosition] = useAtom(draggingPositionAtom);
  const [gettingSingleConfigurationLoading] = useAtom(gettingSingleConfigurationLoadingAtom);
  const [configuratorSingleConfiguration] = useAtom(configuratorSingleConfigurationAtom);
  const [reloadConfigurationAfterInit, setReloadConfigurationAfterInit] = useAtom(reloadConfigurationAfterInitAtom);
  const [, setConfiguratorSelectedCity] = useAtom(configuratorSelectedCityAtom);
  const [, openLoadingModal] = useAtom(openLoadingModalAtom);
  const [, closeModal] = useAtom(closeModalAtom);
  const prevOnlyDetailsRef = useRef(onlyDetails);
  const isUpdatingUrlRef = useRef(false);

  const isOnlyConfigurator = useMemo(() => !configuratorCalculations && !gettingSingleConfigurationLoading, [configuratorCalculations, gettingSingleConfigurationLoading]);
  const isOnlyDetails = useMemo(() => (configuratorCalculations || gettingSingleConfigurationLoading) && onlyDetails, [onlyDetails, configuratorCalculations, gettingSingleConfigurationLoading]);
  const isConfiguring = useMemo(
    () => !!configuratorLayersList?.length || draggingPosition,
    [configuratorLayersList?.length, draggingPosition],
  );

  useEffect(() => {
    if (!reloadConfigurationAfterInit) return;
    const id = reloadConfigurationAfterInit;
    setReloadConfigurationAfterInit(null);
    // Init with the new language has finished. This request returns the result texts in that language.
    getSingleConfiguration({ id }).then();
  }, [reloadConfigurationAfterInit]);

  useEffect(() => {
    if (!isInit) return;
    if (configurationId) {
      // Don't reload configuration if we already have this configuration loaded
      // This prevents reloading layers when URL is updated after calculation
      const currentConfigId = configuratorSingleConfiguration?.id;
      if (isUpdatingUrlRef.current || (currentConfigId != null && String(currentConfigId) === configurationId)) {
        isUpdatingUrlRef.current = false;
        return;
      }
      getSingleConfiguration({ id: configurationId }).then();
    }
  }, [configurationId, isInit]);

  useEffect(() => {
    if (gettingSingleConfigurationLoading) openLoadingModal();
    else closeModal();
  }, [gettingSingleConfigurationLoading]);

  useEffect(() => {
    if (!configurationId) {
      refreshConfiguration();
    }
  }, [configurationId]);

  // Set city only when switching from only_details=true to only_details=false (edit mode).
  // Do not set city when configuratorSingleConfiguration changes after calculation (would overwrite user's selected city).
  useEffect(() => {
    const switchedToEditMode = prevOnlyDetailsRef.current === true && onlyDetails === false;
    prevOnlyDetailsRef.current = onlyDetails;
    if (switchedToEditMode && configurationId && configuratorSingleConfiguration?.city_id && configuratorSingleConfiguration?.city) {
      setConfiguratorSelectedCity({ id: configuratorSingleConfiguration.city_id, name: configuratorSingleConfiguration.city });
    }
  }, [onlyDetails, configurationId, configuratorSingleConfiguration]);

  // After each calculation a new configuration entity is created — update URL with new configuration_id
  useEffect(() => {
    const currentConfigId = configuratorSingleConfiguration?.id;
    if (currentConfigId != null && String(currentConfigId) !== configurationId) {
      isUpdatingUrlRef.current = true; // Mark that we're updating URL ourselves
      const newParams = Object.fromEntries([...searchParams.entries()]);
      newParams.configuration_id = String(currentConfigId);
      setSearchParams(newParams, { replace: true });
    }
  }, [configuratorSingleConfiguration?.id]);

  return (
    <div className={styles.configurator}>
      {!isOnlyDetails && (
        <div className={classNames(styles.topRow, isOnlyConfigurator && styles.topRowOnlyConfigurator)}>
          <ConfiguratorCategories
            isOnlyConfigurator={isOnlyConfigurator}
            draggingProps={{
              onDragStart,
              onDrag,
              onDragStop,
            }}
          />
          <ConfiguratorMain
            isConfiguring={isConfiguring}
            isOnlyConfigurator={isOnlyConfigurator}
            draggingProps={{
              onDragStart,
              onDrag,
              onDragStop,
            }}
            clickingProps={{
              onLayerClick,
              onRemoveLayerClick,
            }}
            refreshConfiguration={refreshConfiguration}
          />
        </div>
      )}

      {!isOnlyConfigurator && (
        <ConfigurationDetails withConfigurator={!isOnlyDetails} refreshConfiguration={refreshConfiguration} />
      )}
    </div>
  );
}

export default Configurator;
